/**
 * @file Briques UI reutilisables : en-tete de feuille, onglets, pastilles de recompense/cout, reconciliation de listes a cles.
 */
import type { Decimal } from '@shared/bignum';
import type { BuffId, RewardBundle, WalletAmounts } from '@config/types';
import type { WalletId } from '@shared/enums';
import { formatBig } from '@shared/format';
import type { UiContext } from '../UiContext';
import { h, toggleClass } from './dom';
import { closeScreen } from './feedback';
import { currencyIcon } from './icons';

/** En-tete standard des ecrans : titre + bouton fermer (data-ui-block). */
export const sheetHeader = (ctx: UiContext, title: string, onClose: () => void = closeScreen): HTMLElement =>
  h(
    'div',
    { class: 'sheet-head' },
    h('h2', { class: 'sheet-title' }, title),
    h('button', { class: 'btn round close', type: 'button', 'aria-label': ctx.i18n.t('common.close'), 'data-ui-block': true, 'data-fast': true, onclick: () => onClose() }, '\u2715'),
  );

export interface TabsHandle<T extends string> {
  readonly el: HTMLElement;
  setActive(id: T): void;
  active(): T;
}

/** Onglets segmentes. */
export const makeTabs = <T extends string>(tabs: ReadonlyArray<{ id: T; label: string; tutorial?: string }>, initial: T, onSelect: (id: T) => void): TabsHandle<T> => {
  const buttons = new Map<T, HTMLElement>();
  let current = initial;
  const el = h('div', { class: 'tabs', role: 'tablist' });
  const paint = (): void => {
    for (const [id, b] of buttons) {
      toggleClass(b, 'active', id === current);
      b.setAttribute('aria-selected', String(id === current));
    }
  };
  for (const t of tabs) {
    const b = h('button', { class: 'tab', type: 'button', role: 'tab', 'data-ui-block': true, 'data-fast': true, 'data-tutorial': t.tutorial, onclick: () => handle.setActive(t.id) }, t.label);
    buttons.set(t.id, b);
    el.append(b);
  }
  const handle: TabsHandle<T> = {
    el,
    setActive(id) {
      if (id === current) return;
      current = id;
      paint();
      onSelect(id);
    },
    active: () => current,
  };
  paint();
  return handle;
};

/** Solde courant (lecture seule) : la seule ecriture possible passe par une Command. */
export const balanceOf = (ctx: UiContext, id: WalletId): Decimal => {
  const balances = ctx.sim.data.wallet;
  return balances[id];
};

/** Pastille "icone + montant". */
export const amountChip = (id: WalletId, amount: Parameters<typeof formatBig>[0], title?: string): HTMLElement => {
  const value = h('b', {}, formatBig(amount));
  return h('span', { class: `chip cur-${id}`, title }, currencyIcon(id), value);
};

export const walletChips = (ctx: UiContext, amounts: WalletAmounts | undefined): HTMLElement[] => {
  const out: HTMLElement[] = [];
  if (!amounts) return out;
  for (const [id, v] of Object.entries(amounts) as Array<[WalletId, NonNullable<WalletAmounts[WalletId]>]>) {
    out.push(amountChip(id, v, ctx.i18n.tDyn(`currency.${id}`)));
  }
  return out;
};

const BUFF_EMOJI: Record<BuffId, string> = { moneyBuff: '\uD83D\uDCB5', powerBuff: '\uD83D\uDCAA', speedBuff: '\u26A1' };

const textChip = (text: string, cls = ''): HTMLElement => h('span', { class: `chip ${cls}`.trim() }, h('b', {}, text));

/** Contenu d'un lot de recompenses (monnaies, tickets, tirages, buffs, XP de passe, arme, skin). */
export const rewardChips = (ctx: UiContext, bundle: RewardBundle): HTMLElement[] => {
  const out = walletChips(ctx, bundle.wallet);
  if (bundle.tickets) for (const [id, n] of Object.entries(bundle.tickets)) out.push(textChip(`\uD83C\uDFAB ${n}`, `ticket-${id}`));
  if (bundle.draws) for (const d of bundle.draws) out.push(textChip(`${d.pool === 'epic' ? '\uD83C\uDF1F' : '\uD83C\uDFB0'} ${d.count}`, `draw-${d.pool}`));
  if (bundle.buffs) for (const b of bundle.buffs) out.push(textChip(`${BUFF_EMOJI[b.id]} ${Math.max(1, Math.round(b.seconds / 60))}m`, 'buff-chip'));
  if (bundle.passXp) out.push(textChip(ctx.i18n.t('pass.xp', { xp: bundle.passXp }), 'xp'));
  if (bundle.weapon) out.push(textChip(`\uD83D\uDD2B ${ctx.i18n.name(ctx.index.weapon(bundle.weapon.defId).name)}`));
  if (bundle.skinId) out.push(textChip(`\uD83D\uDC31 ${ctx.i18n.name(ctx.index.skin(bundle.skinId).name)}`));
  return out;
};

/**
 * Reconciliation par cle : cree les noeuds manquants, retire ceux qui ont disparu, conserve l'identite des noeuds (drag en cours,
 * focus) et ne reordonne le DOM que si l'ordre a change.
 */
export const reconcile = <T>(container: HTMLElement, cache: Map<string, HTMLElement>, items: readonly T[], keyOf: (item: T) => string, create: (item: T) => HTMLElement, update: (el: HTMLElement, item: T) => void): void => {
  const seen = new Set<string>();
  let ref: ChildNode | null = container.firstChild;
  for (const item of items) {
    const key = keyOf(item);
    seen.add(key);
    let node = cache.get(key);
    if (!node) {
      node = create(item);
      cache.set(key, node);
    }
    update(node, item);
    if (node === ref) ref = ref.nextSibling;
    else container.insertBefore(node, ref);
  }
  for (const [key, node] of cache) {
    if (!seen.has(key)) {
      node.remove();
      cache.delete(key);
    }
  }
};
